'use client';

import React, { useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';

const traditionalSteps = [
    { icon: 'DocumentTextIcon', label: 'Current file', desc: 'Working on latest version', color: 'text-muted-foreground' },
    { icon: 'BugAntIcon', label: 'Something broke', desc: 'Tax logic fails in production', color: 'text-red-400' },
    { icon: 'MagnifyingGlassIcon', label: 'Find older version', desc: 'Dig through commit history', color: 'text-muted-foreground' },
    { icon: 'ArrowUturnLeftIcon', label: 'Restore entire file', desc: 'Revert all changes', color: 'text-red-400' },
    { icon: 'ExclamationTriangleIcon', label: 'Lose newer work', desc: '3 hours of progress gone', color: 'text-red-400' },
    { icon: 'WrenchScrewdriverIcon', label: 'Manually reconstruct', desc: 'Piece it back together', color: 'text-muted-foreground' },
];

const capstateSteps = [
    { icon: 'DocumentTextIcon', label: 'Current file', desc: 'Working on latest version', color: 'text-muted-foreground' },
    { icon: 'BugAntIcon', label: 'Something broke', desc: 'Tax logic fails in production', color: 'text-red-400' },
    { icon: 'CursorArrowRaysIcon', label: 'Select the block', desc: 'calculatePrice() at 2:41 PM', color: 'text-primary' },
    { icon: 'ArrowUturnLeftIcon', label: 'Restore only that', desc: 'Everything else untouched', color: 'text-primary' },
];

export default function ProblemSection() {
    const sectionRef = useRef<HTMLElement>(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const cards = entry.target.querySelectorAll<HTMLElement>('.flow-card');
                        cards.forEach((card, i) => {
                            setTimeout(() => {
                                card.style.opacity = '1';
                                card.style.transform = 'translateY(0)';
                            }, i * 80);
                        });
                    }
                });
            },
            { threshold: 0.15 }
        );
        if (sectionRef.current) observer.observe(sectionRef.current);
        return () => observer.disconnect();
    }, []);

    return (
        <section ref={sectionRef} id="problem" className="py-24 px-6 relative">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="max-w-2xl mb-16">
                    <p className="section-label mb-4">The Problem</p>
                    <h2 className="text-section-heading font-extrabold text-foreground mb-5">
                        Sometimes you don&apos;t want{' '}
                        <span className="text-gradient-primary">to go back.</span>
                        <br />
                        You just want{' '}
                        <span className="italic font-light text-foreground">one thing</span> back.
                    </h2>
                    <p className="text-lg text-muted-foreground leading-relaxed">
                        Traditional version control forces an all-or-nothing choice. Revert the whole file
                        and lose your latest work, or keep everything and manually reconstruct the old code.
                    </p>
                </div>

                {/* Side-by-side comparison */}
                <div className="grid md:grid-cols-2 gap-8">
                    {/* Traditional */}
                    <div className="bento-card relative overflow-hidden">
                        <div className="absolute inset-0 bg-red-500/3 pointer-events-none" />
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-8 h-8 rounded-lg bg-red-500/10 flex items-center justify-center">
                                <Icon name="XCircleIcon" size={18} className="text-red-400" />
                            </div>
                            <div>
                                <h3 className="font-bold text-foreground text-sm">Traditional Git Workflow</h3>
                                <p className="font-mono-code text-xs text-muted-foreground">6 painful steps</p>
                            </div>
                        </div>
                        <div className="space-y-2">
                            {traditionalSteps.map((step, i) => (
                                <div
                                    key={i}
                                    className="flow-card flex items-center gap-3 p-3 rounded-xl border border-border bg-card/50 transition-all duration-300"
                                    style={{ opacity: 1, transform: 'translateY(0)' }}
                                >
                                    <div
                                        className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${step.color === 'text-red-400' ? 'bg-red-500/10' : 'bg-muted/50'
                                            }`}
                                    >
                                        <Icon name={step.icon as any} size={16} className={step.color} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className={`text-sm font-semibold ${step.color}`}>{step.label}</p>
                                        <p className="text-xs text-muted-foreground truncate">{step.desc}</p>
                                    </div>
                                    <span className="font-mono-code text-xs text-muted-foreground shrink-0">
                                        {String(i + 1).padStart(2, '0')}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* CapState */}
                    <div className="bento-card relative overflow-hidden">
                        <div className="absolute inset-0 bg-primary/3 pointer-events-none" />
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                                <Icon name="CheckCircleIcon" size={18} className="text-primary" />
                            </div>
                            <div>
                                <h3 className="font-bold text-foreground text-sm">CapState Workflow</h3>
                                <p className="font-mono-code text-xs text-primary">4 clean steps</p>
                            </div>
                        </div>
                        <div className="space-y-2">
                            {capstateSteps.map((step, i) => (
                                <div
                                    key={i}
                                    className="flow-card flex items-center gap-3 p-3 rounded-xl border border-border bg-card/50 transition-all duration-300"
                                    style={{ opacity: 1, transform: 'translateY(0)' }}
                                >
                                    <div
                                        className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${step.color === 'text-primary' ? 'bg-primary/10' : 'bg-muted/50'
                                            }`}
                                    >
                                        <Icon name={step.icon as any} size={16} className={step.color} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className={`text-sm font-semibold ${step.color}`}>{step.label}</p>
                                        <p className="text-xs text-muted-foreground truncate">{step.desc}</p>
                                    </div>
                                    <span className="font-mono-code text-xs text-primary shrink-0">
                                        {String(i + 1).padStart(2, '0')}
                                    </span>
                                </div>
                            ))}

                            {/* Spacer to match height */}
                            <div className="p-3 rounded-xl border border-dashed border-border bg-transparent flex items-center justify-center">
                                <p className="text-xs text-muted-foreground italic">
                                    2 fewer steps. 0 lost work.
                                </p>
                            </div>
                            <div className="p-3 rounded-xl border border-dashed border-border bg-transparent flex items-center justify-center">
                                <p className="text-xs text-primary font-mono-code font-medium">
                                    ✓ Everything else preserved
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Pull quote */}
                <div className="mt-12 text-center">
                    <p className="text-subheading font-extrabold text-foreground">
                        Don&apos;t roll back the file.{' '}
                        <span className="text-gradient-primary">Roll back the code.</span>
                    </p>
                </div>
            </div>
        </section>
    );
}